/**
 * IHS-10 Avg match stats — Cards / corners / fouls / free-kicks per
 * game per team. Tight row rhythm. Each row: home value | icon + label | away value.
 */

import type { ReactElement } from 'react';
import { useTranslation } from '../../../i18n/index.js';
import { useTenantConfig } from '../../../tenant/useTenantConfig.js';
import type {
  IhsAvgMatchStatsData,
  IhsAvgMatchStatsSide,
} from '../../../types/ihs.js';
import { formatDecimalAvg } from '../../../utils/numbers.js';
import { IhsCard } from './IhsCard.js';
import { StatComparisonRow } from '../../primitives/StatComparisonRow.js';
import styles from './IhsAvgMatchStats.module.css';

interface RowSpec {
  key: keyof IhsAvgMatchStatsSide;
  labelKey: string;
  defaultLabel: string;
  icon: ReactElement;
}

const YellowCardIcon = (): ReactElement => (
  <span className={`${styles.iconCard} ${styles.iconYellow}`} aria-hidden="true" />
);
const RedCardIcon = (): ReactElement => (
  <span className={`${styles.iconCard} ${styles.iconRed}`} aria-hidden="true" />
);
/* Icon paints go through `style`, not presentation attributes — SVG
   attributes do not resolve `var()`, so the token references below only
   take effect (and pick up brand overrides) in a CSS context. */
const CornerFlagIcon = (): ReactElement => (
  <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
    <line x1="3" y1="1" x2="3" y2="13" style={{ stroke: 'var(--text-primary, #1A1A1A)' }} strokeWidth="1.2" />
    <path d="M3 2 L11 4 L3 6 Z" style={{ fill: 'var(--status-negative, #c12121)' }} />
  </svg>
);
const FoulIcon = (): ReactElement => (
  <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
    <line x1="3" y1="3" x2="11" y2="11" style={{ stroke: 'var(--accent-primary, #2A46D4)' }} strokeWidth="2" />
    <line x1="11" y1="3" x2="3" y2="11" style={{ stroke: 'var(--accent-primary, #2A46D4)' }} strokeWidth="2" />
  </svg>
);
const FreeKickIcon = (): ReactElement => (
  <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
    <circle cx="7" cy="5" r="2" fill="none" style={{ stroke: 'var(--accent-primary, #2A46D4)' }} strokeWidth="1.2" />
    <line x1="7" y1="7" x2="7" y2="11" style={{ stroke: 'var(--accent-primary, #2A46D4)' }} strokeWidth="1.2" />
    <line x1="4" y1="13" x2="10" y2="13" style={{ stroke: 'var(--accent-primary, #2A46D4)' }} strokeWidth="1.2" />
  </svg>
);

const ROWS: RowSpec[] = [
  {
    key: 'yellowCardsPerGame',
    labelKey: 'ihs.avgMatchStats.yellowCards',
    defaultLabel: 'Yellow Cards',
    icon: <YellowCardIcon />,
  },
  {
    key: 'redCardsPerGame',
    labelKey: 'ihs.avgMatchStats.redCards',
    defaultLabel: 'Red Cards',
    icon: <RedCardIcon />,
  },
  {
    key: 'freeKicksPerGame',
    labelKey: 'ihs.avgMatchStats.freeKicks',
    defaultLabel: 'Free kicks',
    icon: <FreeKickIcon />,
  },
  {
    key: 'cornersPerGame',
    labelKey: 'ihs.avgMatchStats.corners',
    defaultLabel: 'Corners',
    icon: <CornerFlagIcon />,
  },
  {
    key: 'foulsPerGame',
    labelKey: 'ihs.avgMatchStats.fouls',
    defaultLabel: 'Fouls',
    icon: <FoulIcon />,
  },
];

export function IhsAvgMatchStats({
  data,
}: {
  data: IhsAvgMatchStatsData;
}): ReactElement {
  const { t } = useTranslation();
  const tenant = useTenantConfig();
  const decimalSeparator =
    tenant.kind === 'live' || tenant.kind === 'paused'
      ? tenant.config.market.decimalSeparator
      : '.';
  const title =
    data.header?.title ??
    t('ihs.avgMatchStats.title', { defaultValue: 'Average match statistics' });
  return (
    <IhsCard title={title}>
      <div className={styles.rows}>
        {ROWS.map(({ key, labelKey, defaultLabel, icon }) => {
          const home = data.home[key] as number | null;
          const away = data.away[key] as number | null;
          // League-average marker (2026-08-01): engine ships per-match
          // league means where the baselines corpus has them.
          const avg = (data as { leagueAvg?: Record<string, number> }).leagueAvg?.[key as string];
          const avgProps =
            typeof avg === 'number' && Number.isFinite(avg)
              ? {
                  leagueAvg: avg,
                  leagueAvgLabel: t('stat.leagueAvg', {
                    defaultValue: 'Lg avg {{value}}',
                    value: formatDecimalAvg(avg, decimalSeparator),
                  }),
                }
              : {};
          return (
            <StatComparisonRow
              key={key as string}
              label={t(labelKey, { defaultValue: defaultLabel })}
              labelIcon={icon}
              home={formatDecimalAvg(home, decimalSeparator)}
              away={formatDecimalAvg(away, decimalSeparator)}
              {...avgProps}
              // Per-match averages are plain counts — numbers only (Stat
              // Row v2: bars are explicit-ask, never value-parsed).
              renderType="numbers"
              animateOnInView
            />
          );
        })}
      </div>
    </IhsCard>
  );
}
